import { type GenericMap, type LabelValue } from "@common/declarations";
import date from "@common/lib/date";
import { type I18n } from "@common/lib/i18n";
import { toCamelCase } from "@common/lib/misc";
import { filterAggregates } from "@common/modules/data/lib";
import { type Data } from "@common/modules/data/types";
import Network from "@common/modules/network/types";
import { groupBy, omit, sum, sumBy, toPairs, values } from "lodash";
import { type ChartTypeItem, type ExtraGridConfigColumn } from "../types";
import { type NetworkVolumeReportConfig, type NetworkVolumeReportStackBy } from "./Static/NetworkVolume";
import { type TotalSaleBase } from "./types";

export const getYAxis = (
  i18n: I18n,
  config: NetworkVolumeReportConfig,
  ranks: Network.Api.RankOptionItem[],
  data: NetworkVolumeAggregate[]
): LabelValue<string>[] => {
  // collect data keys
  const keys = new Set<string>();
  for (const item of data) {
    const obj = omit(item, "currencyCode", "total", config.group.by);
    for (const key of Object.keys(obj)) {
      keys.add(key);
    }
  }

  return [...keys].map((x) => ({
    label:
      config.stackBy === "Rank"
        ? ranks.find((y) => y.value === x)?.label ?? x
        : config.stackBy === "Volume"
        ? i18n[x]
        : config.stackBy
        ? `${i18n[config.stackBy]} ${x}`
        : i18n[x],
    value: x
  }));
};

export type NetworkVolumeAggregate = GenericMap<any> & {
  currencyCode: string;
  total: number;
};

const accumulate = (
  config: NetworkVolumeReportConfig,
  data: Data.Api.GenealogyCycleAggregate[],
  stack: NetworkVolumeReportStackBy | undefined | null
): GenericMap<number> => {
  const by = config.aggregateBy;

  switch (stack) {
    case "BreakawayLevel":
    case "DifferentialLevel":
    case "Leg":
    case "Level": {
      return toPairs(groupBy(data, (y) => y[toCamelCase(stack)])).reduce(
        (res, y) => ({
          ...res,
          [y[0]]: sumBy(y[1], (z) => z[by])
        }),
        {}
      );
    }
    case "Rank": {
      const byRankId = groupBy(
        data.filter((y) => y.rankId),
        (y) => y.rankId
      );
      return toPairs(byRankId).reduce(
        (res, y) => ({
          ...res,
          [y[0]]: sumBy(y[1], (z) => z[by])
        }),
        {}
      );
    }
    case "Volume": {
      const res: GenericMap<any> = {};
      if (!config.volumeTypes || config.volumeTypes.includes("Banked")) {
        res.Banked = sumBy(
          data.filter((y) => y.isBanked),
          (y) => y[by]
        );
      }
      if (!config.volumeTypes || config.volumeTypes.includes("Personal")) {
        res.Personal = sumBy(
          data.filter((y) => y.isPersonal === true),
          (y) => y[by]
        );
      }
      if (!config.volumeTypes || config.volumeTypes.includes("Team")) {
        res.Team = sumBy(
          data.filter((y) => y.isPersonal === false),
          (y) => y[by]
        );
      }
      return res;
    }
    default: {
      return {
        [by]: sumBy(data, (y) => y[by])
      };
    }
  }
};

export const processAggregates = (
  data: Data.Api.GenealogyCycleAggregate[],
  config: NetworkVolumeReportConfig,
  rankOptions: Network.Api.RankOptionItem[],
  currencyCode: string
): NetworkVolumeAggregate[] => {
  const filtered = filterAggregates(data, config, rankOptions);
  const grouped = groupBy(filtered, config.group.by);

  return toPairs(grouped).map((x) => {
    // NOTE: it's not clear how grouping by Rank w/o stacking should be displayed
    const accumulates = accumulate(
      config,
      x[1],
      config.group.by === "rankName" && !config.stackBy ? "Volume" : config.stackBy
    );

    return {
      [config.group.by]: x[0],
      ...accumulates,
      total: sum(values(accumulates)),
      currencyCode
    };
  });
};

export const getDateType = (from: Date, to: Date, dataLength: number) => {
  const differenceInDays = date.differenceInDays(to, from);
  if (differenceInDays < 120) {
    return date.isSameWeek(from, to)
      ? "dayName"
      : date.isSameMonth(from, to)
      ? "day"
      : date.isSameYear(from, to)
      ? "monthDay"
      : "date";
  }

  return dataLength > 1400 ? "year" : date.isSameYear(from, to) ? "monthName" : "yearMonth";
};

export const getTotalSaleExtraColumns = (i18n: I18n): ExtraGridConfigColumn<TotalSaleBase>[] => [
  {
    dataType: "number",
    key: "orderCount",
    label: i18n.orders,
    data: (x) => x.orderCount,
    isDefault: true
  },
  {
    dataType: "decimal2",
    key: "surchargeDiscountAmount",
    label: i18n.surchargeDiscounts
  },
  {
    dataType: "decimal2",
    key: "surchargeTaxAmount",
    label: i18n.surchargeTax
  },
  {
    dataType: "decimal2",
    key: "surchargeAmount",
    label: i18n.surchargeWithoutTaxInfo
  },
  {
    dataType: "decimal2",
    key: "surchargeWithTaxAmount",
    label: i18n.surchargeWithTaxInfo
  },
  {
    dataType: "decimal2",
    key: "shippingDiscountAmount",
    label: i18n.shippingDiscounts
  },
  {
    dataType: "decimal2",
    key: "shippingTaxAmount",
    label: i18n.shippingTax
  },
  {
    dataType: "decimal2",
    key: "shippingAmount",
    label: i18n.shippingWithoutTaxInfo
  },
  {
    dataType: "decimal2",
    key: "shippingWithTaxAmount",
    label: i18n.shippingWithTaxInfo
  },
  {
    dataType: "decimal2",
    key: "salesDiscountAmount",
    label: i18n.salesDiscounts
  },
  {
    dataType: "decimal2",
    key: "salesTaxAmount",
    label: i18n.salesTax
  },
  {
    dataType: "decimal2",
    key: "salesAmount",
    label: i18n.salesWithoutTaxInfo
  },
  {
    dataType: "decimal2",
    key: "salesWithTaxAmount",
    label: i18n.salesWithTaxInfo
  },
  {
    dataType: "decimal2",
    key: "netSales",
    label: i18n.netSales,
    isDefault: true
  },
  {
    dataType: "decimal2",
    key: "netShipping",
    label: i18n.netShipping,
    isDefault: true
  },
  {
    dataType: "decimal2",
    key: "netSurcharges",
    label: i18n.netSurcharges,
    isDefault: true
  },
  {
    dataType: "decimal2",
    key: "netTax",
    label: i18n.netTax,
    isDefault: true
  },
  {
    dataType: "decimal2",
    key: "totalSales",
    label: i18n.totalSales,
    isDefault: true
  }
];

export const totalSaleChartTypes: ChartTypeItem<TotalSaleBase>[] = [
  { type: "Bar" },
  { type: "HorizontalBar" },
  { type: "Scatter", key: "orderCount", stepSize: 1 }
];
